"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { CheckCircle, AlertCircle, Loader2, Mail } from "lucide-react";
import Badge from "@components/ui/Badge";
import {
  trackFormSubmit,
  trackError,
  trackGoogleAdsConversion,
} from "@utils/ga4Tracking";
import webinarNewsletterFormData from "@data/components/webinar-newsletter-form.json";

interface GoogleAdsConversionConfig {
  conversionId: string;
  conversionLabel: string;
  page: string;
}

interface WebinarNewsletterFormProps {
  title?: string;
  description?: string;
  subtitle?: string;
  variant?: "full" | "compact" | "modal";
  webhookSource?: string;
  onClose?: () => void;
  hideNameField?: boolean;
  googleAdsConversion?: GoogleAdsConversionConfig;
}

interface WebinarNewsletterFormData {
  fields: {
    email: {
      label: string;
      placeholder: string;
      required: boolean;
    };
    name: {
      label: string;
      placeholder: string;
      required: boolean;
    };
  };
  submitText: string;
  successMessage: string;
  errorMessage: string;
  webhook: {
    source: string;
  };
}

export default function WebinarNewsletterForm({
  title,
  description,
  subtitle,
  variant = "full",
  webhookSource,
  onClose,
  hideNameField = false,
  googleAdsConversion,
}: WebinarNewsletterFormProps) {
  const formData = webinarNewsletterFormData as WebinarNewsletterFormData;
  const pathname = usePathname();
  const resolvedSource =
    webhookSource ?? (pathname ? `Neue Registrierung über ${pathname}` : "Neue Registrierung über Webseite DBI");
  const isModalVariant = variant === "modal";
  
  const [formState, setFormState] = useState({
    name: "",
    email: "",
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (submitStatus === "error") {
      setSubmitStatus("idle");
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    if (!gdprConsent) return;

    try {
      const webhookData = {
        name: formState.name.trim(),
        email: formState.email.trim().toLowerCase(),
        gdprConsent: true,
        timestamp: new Date().toISOString(),
        source: resolvedSource,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        formType: "webinar_newsletter_signup",
        submissionId: `newsletter_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      const response = await fetch("/api/webinar-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(webhookData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        trackFormSubmit("webinar_newsletter_signup", "newsletter", true);
        if (googleAdsConversion) {
          // Enhanced Conversions: `webhookData` holds the already-normalised
          // values and survives the `setFormState` reset two lines down.
          trackGoogleAdsConversion(
            googleAdsConversion.conversionId,
            googleAdsConversion.conversionLabel,
            googleAdsConversion.page,
            {
              email: webhookData.email,
              name: webhookData.name,
            }
          );
        }

        setFormState({ name: "", email: "" });
        setGdprConsent(false);

        // Auto-close modal after success
        if (isModalVariant && onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("❌ Webinar newsletter signup submission failed:", error);
      setSubmitStatus("error");
      setErrorMessage(formData.errorMessage);
      trackFormSubmit("webinar_newsletter_signup", "newsletter", false);
      trackError(
        "webinar_newsletter_form_submission",
        error instanceof Error ? error.message : "Unknown error",
        "webinar-newsletter"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal variant - just the form without card wrapper
  if (isModalVariant) {
    return (
      <div className="w-full">
        {submitStatus === "success" ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {formData.successMessage}
            </h3>
            <p className="text-gray-600">
              Sie erhalten in Kürze eine Bestätigungs-E-Mail.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!hideNameField && (
            <div>
              <label
                htmlFor="newsletter-name-modal"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {formData.fields.name.label}
                {formData.fields.name.required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input
                type="text"
                id="newsletter-name-modal"
                name="name"
                required={formData.fields.name.required}
                value={formState.name}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder={formData.fields.name.placeholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-abis-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              />
            </div>
            )}

            <div>
              <label
                htmlFor="newsletter-email-modal"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {formData.fields.email.label}
                {formData.fields.email.required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input
                type="email"
                id="newsletter-email-modal"
                name="email"
                required={formData.fields.email.required}
                value={formState.email}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder={formData.fields.email.placeholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-abis-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              />
            </div>

            {submitStatus === "error" && errorMessage && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <label htmlFor="gdpr-consent-modal" className="flex items-start gap-3 cursor-pointer group py-1">
              <input
                type="checkbox"
                id="gdpr-consent-modal"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                className="sr-only"
              />
              <div className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                gdprConsent
                  ? "bg-abis-primary"
                  : "bg-gray-100 border-2 border-gray-300 group-hover:border-abis-primary/50"
              }`}>
                {gdprConsent && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <span className="text-xs text-gray-500 leading-relaxed group-hover:text-gray-700 transition-colors">
              *Ich stimme der Verarbeitung meiner Daten gemäß der{" "}
                <Link href="/imprint#datenschutz" className="text-abis-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Datenschutzerklärung 
                </Link>{" "}
                (DSGVO) zu.
              </span>
            </label>

            <button
              type="submit"
              disabled={isSubmitting || !gdprConsent}
              className="w-full bg-abis-accent hover:bg-abis-accent/90 text-abis-primary font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-abis-accent/30 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              aria-label={formData.submitText}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-lg">Wird gesendet...</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span className="text-lg">{formData.submitText}</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    );
  }

  // Card variant - full card with title, description, etc.
  const containerClasses =
    variant === "full"
      ? "w-full"
      : "w-full md:w-[calc(33.333%-1.33rem)]";

  return (
    <div className={containerClasses}>
      <div className="relative h-full bg-gradient-to-br from-abis-primary via-abis-primary/95 to-abis-primary/90 rounded-xl shadow-xl overflow-hidden border border-abis-primary/20 group hover:shadow-2xl transition-all duration-300">
        {/* Background Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: "24px 24px",
          }}
          aria-hidden="true"
        ></div>

        {/* Gradient Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-abis-accent/5 via-transparent to-abis-primary/10"
          aria-hidden="true"
        ></div>

        {/* Content */}
        <div className="relative z-10 p-8 md:p-10 h-full flex flex-col justify-between">
          <div>
            {subtitle && (
              <Badge
                variant="white"
                size="sm"
                backdropBlur
                className="mb-4"
                icon={null}
              >
                {subtitle}
              </Badge>
            )}

            <h3
              className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight"
              style={{
                fontFamily: "var(--font-kanit, Kanit, sans-serif)",
              }}
            >
              {title}
            </h3>

            <p className="text-white/90 text-base md:text-lg leading-relaxed mb-6">
              {description}
            </p>
          </div>

          {submitStatus === "success" ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-abis-accent mx-auto mb-3" />
              <p className="text-white font-semibold text-lg">
                {formData.successMessage}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!hideNameField && (
              <div>
                <label
                  htmlFor="newsletter-name"
                  className="block text-sm font-medium text-white/90 mb-2"
                >
                  {formData.fields.name.label}
                  {formData.fields.name.required && (
                    <span className="text-abis-accent ml-1">*</span>
                  )}
                </label>
                <input
                  type="text"
                  id="newsletter-name"
                  name="name"
                  required={formData.fields.name.required}
                  value={formState.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder={formData.fields.name.placeholder}
                  className="w-full px-4 py-3 border border-white/20 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-abis-accent focus:border-abis-accent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
              </div>
              )}

              <div>
                <label
                  htmlFor="newsletter-email"
                  className="block text-sm font-medium text-white/90 mb-2"
                >
                  {formData.fields.email.label}
                  {formData.fields.email.required && (
                    <span className="text-abis-accent ml-1">*</span>
                  )}
                </label>
                <input
                  type="email"
                  id="newsletter-email"
                  name="email"
                  required={formData.fields.email.required}
                  value={formState.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder={formData.fields.email.placeholder}
                  className="w-full px-4 py-3 border border-white/20 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-abis-accent focus:border-abis-accent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
              </div>

              {submitStatus === "error" && errorMessage && (
                <div className="flex items-center gap-2 text-abis-accent text-sm bg-white/10 p-3 rounded-lg border border-abis-accent/30">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <label htmlFor="gdpr-consent-full" className="flex items-start gap-3 cursor-pointer group py-1">
                <input
                  type="checkbox"
                  id="gdpr-consent-full"
                  checked={gdprConsent}
                  onChange={(e) => setGdprConsent(e.target.checked)}
                  className="sr-only"
                />
                <div className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                  gdprConsent
                    ? "bg-abis-accent"
                    : "bg-white/10 border-2 border-white/30 group-hover:border-abis-accent/50"
                }`}>
                  {gdprConsent && <CheckCircle className="w-3.5 h-3.5 text-abis-primary" />}
                </div>
                <span className="text-xs text-white/70 leading-relaxed group-hover:text-white/90 transition-colors">
                  *Ich stimme der Verarbeitung meiner Daten gemäß der{" "}
                  <Link href="/imprint#datenschutz" className="text-abis-accent hover:underline" target="_blank" rel="noopener noreferrer">
                    Datenschutzerklärung
                  </Link>{" "}
                  (DSGVO) zu.
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting || !gdprConsent}
                className="group/btn w-full bg-abis-accent hover:bg-abis-accent/90 text-abis-primary font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-abis-accent/30 focus:ring-offset-2 focus:ring-offset-abis-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                aria-label={formData.submitText}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-lg">Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    <span className="text-lg">{formData.submitText}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Decorative Elements */}
        <div
          className="absolute top-0 right-0 w-32 h-32 bg-abis-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"
          aria-hidden="true"
        ></div>
        <div
          className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2"
          aria-hidden="true"
        ></div>
      </div>
    </div>
  );
}
